import * as React from 'react';
import { ArrowUpDown } from 'lucide-react';
import { cn } from './lib/utils';
import { DomainCard } from './DomainCard';
import { EmptyState } from './EmptyState';

/**
 * Design-system-compliant data table (§7c).
 *
 * Supports: sortable headers, colgroup widths, sticky headers,
 * row tinting, checkbox selection, clickable rows, empty state.
 *
 * @param {object} props
 * @param {ColumnDef[]} props.columns
 * @param {object[]} props.data
 * @param {(row) => string} [props.rowKey] — unique key extractor (default: row.id)
 * @param {string} [props.domain] — wraps in <DomainCard domain="...">
 * @param {boolean} [props.stickyHeader=false]
 * @param {(row) => void} [props.onRowClick]
 * @param {(row) => "critical"|"warning"|null} [props.rowTint]
 * @param {string} [props.selectedRowKey] — highlights this row
 * @param {{ sortKey, sortAsc, toggleSort }} [props.sort]
 * @param {boolean} [props.selectable=false]
 * @param {Set} [props.selectedIds]
 * @param {(id) => void} [props.onToggleSelect]
 * @param {(allSelected: boolean) => void} [props.onToggleAll]
 * @param {string} [props.emptyTitle="No data"]
 * @param {string} [props.emptyBody]
 * @param {React.ReactNode} [props.footer]
 * @param {string} [props.className]
 */
function DataTable({
  columns,
  data,
  rowKey = (row) => row.id,
  domain,
  stickyHeader = false,
  onRowClick,
  rowTint,
  selectedRowKey,
  sort,
  selectable = false,
  selectedIds,
  onToggleSelect,
  onToggleAll,
  emptyTitle = 'No data',
  emptyBody,
  footer,
  className,
}) {
  const hasWidths = columns.some((c) => c.width);
  const allSelected = selectable && data.length > 0 && selectedIds?.size === data.length;

  const TINT = {
    critical: 'bg-[var(--red-bg)]',
    warning: 'bg-[var(--yellow-bg)]',
  };

  const table = (
    <div className={cn('overflow-x-auto', className)}>
      <table className={cn('w-full text-body', hasWidths && 'table-fixed')}>
        {hasWidths && (
          <colgroup>
            {selectable && <col className="w-8" />}
            {columns.map((col) => (
              <col key={col.key} className={col.width ? `w-[${col.width}]` : undefined} style={col.width ? { width: col.width } : undefined} />
            ))}
          </colgroup>
        )}
        <thead>
          <tr className="border-b border-nn-border">
            {selectable && (
              <th
                className={cn(
                  'py-2.5 px-3 text-left',
                  stickyHeader && 'sticky top-0 z-10 bg-slate-200'
                )}
              >
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => onToggleAll?.(!allSelected)}
                  onClick={(e) => e.stopPropagation()}
                  className="accent-aqua"
                />
              </th>
            )}
            {columns.map((col) => {
              const sortable = col.sortable !== false && sort;
              const isActive = sort?.sortKey === col.key;
              const align = col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';

              return (
                <th
                  key={col.key}
                  className={cn(
                    'text-caption font-bold text-text-muted uppercase tracking-wider',
                    'py-2.5 px-3',
                    align,
                    sortable && 'cursor-pointer select-none hover:text-text-dark transition-colors',
                    stickyHeader && 'sticky top-0 z-10 bg-slate-200',
                    col.headerClassName
                  )}
                  onClick={sortable ? () => sort.toggleSort(col.key) : undefined}
                >
                  {sortable ? (
                    <div className={cn('flex items-center gap-1', col.align === 'right' && 'justify-end')}>
                      {col.label}
                      <ArrowUpDown
                        size={10}
                        className={isActive ? 'text-aqua' : 'text-text-muted/50'}
                      />
                    </div>
                  ) : (
                    col.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (selectable ? 1 : 0)} className="py-6">
                <EmptyState title={emptyTitle} body={emptyBody} size="sm" />
              </td>
            </tr>
          ) : (
            data.map((row) => {
              const key = rowKey(row);
              const tint = rowTint?.(row);
              const isSelected = selectedRowKey != null && key === selectedRowKey;

              return (
                <tr
                  key={key}
                  className={cn(
                    'border-b border-nn-border last:border-0',
                    onRowClick && 'cursor-pointer hover:bg-surface-warm transition-colors',
                    tint && TINT[tint],
                    isSelected && 'ring-1 ring-inset ring-aqua'
                  )}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {selectable && (
                    <td className="py-2.5 px-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds?.has(key) ?? false}
                        onChange={() => onToggleSelect?.(key)}
                        className="accent-aqua"
                      />
                    </td>
                  )}
                  {columns.map((col) => {
                    const value = row[col.key];
                    const align = col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';

                    return (
                      <td
                        key={col.key}
                        className={cn('py-2.5 px-3', align, col.className)}
                      >
                        {col.render ? col.render(value, row) : (value ?? '—')}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
        {footer && (
          <tfoot>{footer}</tfoot>
        )}
      </table>
    </div>
  );

  if (domain) {
    return <DomainCard domain={domain}>{table}</DomainCard>;
  }

  return table;
}

export { DataTable };
